import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyDeletionCheck } from './fetchCompanyDeletionCheck';
import { type CompanyDeletionCheckResponse } from './types';

type CompanyDeletionCheckOptions<TData> = Omit<
  UseQueryOptions<CompanyDeletionCheckResponse, Error, TData>,
  'queryFn' | 'queryKey'
>;

/**
 * Creates reusable query options for checking whether a company can be deleted.
 *
 * @param parameters - The optional company ID and additional query options.
 * @returns Query options that remain skipped until the company ID is available.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/check_company_deletion_vadmin_companies__company_id__deletion_check_get
 */
// fallow-ignore-next-line unused-export -- intended public API surface for loaders and prefetching
export function getCompanyDeletionCheckQueryOptions<
  TData = CompanyDeletionCheckResponse,
>({
  companyId,
  options,
}: {
  companyId: UUID | undefined;
  options?: CompanyDeletionCheckOptions<TData>;
}) {
  return queryOptions({
    queryFn: companyId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchCompanyDeletionCheck(companyId, { signal })
      : skipToken,
    queryKey: queryKeysCompany.getDeletionCheck({
      companyId: companyId ?? '',
    }),
    staleTime: ms('30s'),
    ...options,
  });
}

/**
 * Checks whether a company can be deleted.
 *
 * @param companyId - The optional company ID to check.
 * @param options - Additional TanStack Query options.
 * @returns The company deletion-check query result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/check_company_deletion_vadmin_companies__company_id__deletion_check_get
 */
export function useQueryCompanyDeletionCheck<
  TData = CompanyDeletionCheckResponse,
>(companyId: UUID | undefined, options?: CompanyDeletionCheckOptions<TData>) {
  return useQuery(
    getCompanyDeletionCheckQueryOptions<TData>({ companyId, options }),
  );
}
